import React, { useState, useEffect } from "react";
import "./FuturisticDashboard.css";
import prevIcon from "../../../assets/svg/pre-icon.svg";
import nextIcon from "../../../assets/svg/next-icon.svg";
import {
  getAvailableDockPredictionApi,
  getDockInPredictionApi,
  getQueuePredictionApi,
  getAllDocksApi,
  getLocationPredictionApi,
  getLocationFreePredictionApi,
  getLocationListApi,
} from "./futuristic-api.js";
// Available Chance Prediction Component
const AvailableChancePrediction = ({ chancePrediction = [] }) => {
  const [selectedTime, setSelectedTime] = React.useState("9 AM");
  const [selectedDate, setSelectedDate] = React.useState("27 Feb 2025");
  const scrollContainerRef = React.useRef(null);
  const timeScrollRef = React.useRef(null);

  const timeSlots = [
    "9 AM",
    "10 AM",
    "11 AM",
    "12 PM",
    "1 PM",
    "2 PM",
    "3 PM",
    "4 PM",
    "5 PM",
    "6 PM",
    "7 PM",
    "8 PM",
    "9 PM",
    "10 PM",
    "11 PM",
    "12 PM",
  ];

  // Function to get color based on chance percentage
  const getChanceColor = (chance) => {
    if (chance >= 95) return "green";
    if (chance >= 70) return "orange";
    return "red";
  };

  const days = chancePrediction.map((dock) => ({
    ...dock,
    color: getChanceColor(dock.chance),
  }));

  const handleScrollDays = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 350; // Scroll by approximately 3 cards
      const currentScroll = scrollContainerRef.current.scrollLeft;
      const targetScroll =
        direction === "left"
          ? currentScroll - scrollAmount
          : currentScroll + scrollAmount;

      scrollContainerRef.current.scrollTo({
        left: targetScroll,
        behavior: "smooth",
      });
    }
  };

  const handleScrollTimeSlots = (direction) => {
    if (timeScrollRef.current) {
      const scrollAmount = 200; // Scroll by approximately 2-3 time slots
      const currentScroll = timeScrollRef.current.scrollLeft;
      const targetScroll =
        direction === "left"
          ? currentScroll - scrollAmount
          : currentScroll + scrollAmount;

      timeScrollRef.current.scrollTo({
        left: targetScroll,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="chance-prediction-container">
      <div className="chance-top-bar">
        <h2 className="chance-main-title">Available chance predication</h2>

        <div className="chance-controls">
          <button
            className="chance-nav-arrow"
            onClick={() => handleScrollTimeSlots("left")}
          >
            <img src={prevIcon} alt="prev" />
          </button>

          <div className="chance-time-selector" ref={timeScrollRef}>
            {timeSlots.map((time) => (
              <button
                key={time}
                className={`chance-time-btn ${
                  selectedTime === time ? "active" : ""
                }`}
                onClick={() => setSelectedTime(time)}
              >
                {time}
              </button>
            ))}
          </div>

          <button
            className="chance-nav-arrow"
            onClick={() => handleScrollTimeSlots("right")}
          >
            <img src={nextIcon} alt="next" />
          </button>

          <div className="chance-date-picker">
            <span className="chance-selected-date">{selectedDate}</span>
            <button className="chance-dropdown-icon">▾</button>
          </div>
        </div>
      </div>

      <div className="chance-days-container">
        <button
          className="chance-scroll-arrow left"
          onClick={() => handleScrollDays("left")}
        >
          <img src={prevIcon} alt="prev" />
        </button>

        <div className="chance-days-strip" ref={scrollContainerRef}>
          {days.map((day) => (
            <div key={day.id} className={`chance-day-card chance-${day.color}`}>
              <div className="chance-day-id">{day.dock ?? ""}</div>
              <div className="chance-day-percent">{day.chance}% chance</div>
            </div>
          ))}
        </div>

        <button
          className="chance-scroll-arrow right"
          onClick={() => handleScrollDays("right")}
        >
          <img src={nextIcon} alt="next" />
        </button>
      </div>
    </div>
  );
};

// Dock Status Card Component
const DockStatusCard = ({ title, value, increase, peakDate, graphData }) => {
  const [hoveredPoint, setHoveredPoint] = React.useState(null);
  const [tooltipPos, setTooltipPos] = React.useState({ x: 0, y: 0 });

  const handlePointHover = (index, value, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setHoveredPoint({ index, value });
    setTooltipPos({
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    });
  };

  // Generate smooth curve path
  const createSmoothPath = (data, closed = false) => {
    if (data.length === 0) return "";

    let path = `M 0 ${100 - data[0]}`;

    // Create smooth curve through points
    for (let i = 0; i < data.length - 1; i++) {
      const x1 = (i / (data.length - 1)) * 300;
      const y1 = 100 - data[i];
      const x2 = ((i + 1) / (data.length - 1)) * 300;
      const y2 = 100 - data[i + 1];

      const cx = x1 + (x2 - x1) * 0.5;
      const cy1 = y1;
      const cy2 = y2;

      path += ` C ${cx} ${cy1}, ${cx} ${cy2}, ${x2} ${y2}`;
    }

    if (closed) {
      path += " L 300 100 L 0 100 Z";
    }

    return path;
  };

  return (
    <div className="fs-card dock-status-card">
      <div className="dock-status-title">{title}</div>
      <div className="dock-status-value">{value}</div>
      <div className="dock-increase-badge">+{increase}%</div>
      <div className="dock-increase-text">will be increased from present</div>

      <div className="dock-graph">
        <svg
          width="100%"
          height="74"
          viewBox="0 0 300 100"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient
              id={`grad-${title.replace(/\s+/g, "-")}`}
              x1="0%"
              y1="0%"
              x2="0%"
              y2="100%"
            >
              <stop offset="0%" stopColor="#fca5a5" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#fca5a5" stopOpacity="0.05" />
            </linearGradient>
          </defs>

          {/* Filled area */}
          <path
            d={createSmoothPath(graphData, true)}
            fill={`url(#grad-${title.replace(/\s+/g, "-")})`}
          />

          {/* Stroke line */}
          <path
            d={createSmoothPath(graphData, false)}
            fill="none"
            stroke="#D35258"
            strokeWidth="2"
          />

          {/* Interactive points */}
          {/* {graphData.map((point, index) => (
            <circle
              key={index}
              cx={(index / (graphData.length - 1)) * 300}
              cy={100 - point}
              r="5"
              fill="#ffffff"
              stroke="#D3525800"
              strokeWidth="2.5"
              onMouseEnter={(e) => handlePointHover(index, point, e)}
              onMouseLeave={() => setHoveredPoint(null)}
              style={{ cursor: "pointer", transition: "all 0.2s ease" }}
              className="graph-point"
            />
          ))} */}
        </svg>
      </div>

      {hoveredPoint !== null && (
        <div
          className="dock-tooltip"
          style={{
            position: "fixed",
            left: `${tooltipPos.x}px`,
            top: `${tooltipPos.y}px`,
            transform: "translate(-50%, -100%)",
          }}
        >
          <div className="tooltip-value">{hoveredPoint.value}</div>
          <div className="tooltip-date">{peakDate}</div>
        </div>
      )}
    </div>
  );
};

// Date-based Dock In Prediction Component (Heatmap)
const DateBasedDockPrediction = ({ predictionData = {} }) => {
  const [hoveredCell, setHoveredCell] = React.useState(null);
  const [tooltipPos, setTooltipPos] = React.useState({ x: 0, y: 0 });
  const dockList = predictionData.docks ?? [];
  // Extract dates from first dock's predictions
  const dates =
    dockList.length > 0 ? dockList[0].predict.map((p) => p.date) : [];

  // Extract dock names
  const days = dockList.length > 0 ? dockList.map((d) => d.dock) : [];

  // Function to determine level based on count and legend thresholds
  const getCountLevel = (count) => {
    if (count === 0) return "normal";
    if (count >= predictionData.legend.high) return "high";
    if (count >= predictionData.legend.moderate) return "moderate";
    return "normal";
  };

  // Transform predict data into heatmap format
  const heatmapData = dockList.map((dock) =>
    dock.predict.map((p) => getCountLevel(p.count))
  );

  const getCellValue = (rowIdx, colIdx) => {
    return dockList[rowIdx]?.predict[colIdx]?.count || 0;
  };

  const handleCellHover = (rowIdx, colIdx, event) => {
    const level = heatmapData[rowIdx][colIdx];
    if (level) {
      const rect = event.currentTarget.getBoundingClientRect();
      const count = getCellValue(rowIdx, colIdx);
      setHoveredCell({ value: count, level });
      setTooltipPos({
        x: rect.left + rect.width / 2,
        y: rect.top - 10,
      });
    }
  };

  return (
    <div className="fs-card date-dock-prediction-card">
      <div className="date-dock-header">
        <h3 className="date-dock-title">Date based dock in prediction</h3>
        <div className="date-dock-legend">
          <div className="legend-item">
            <span className="legend-dot legend-normal"></span>
            <span className="legend-text">Normal</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot legend-moderate"></span>
            <span className="legend-text">Moderate</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot legend-high"></span>
            <span className="legend-text">High</span>
          </div>
        </div>
      </div>

      <div className="date-dock-grid-container">
        <div className="date-dock-days-column">
          {days.map((day) => (
            <div key={day} className="date-dock-day-label">
              {day}
            </div>
          ))}
        </div>

        <div className="date-dock-content">
          <div className="date-dock-grid">
            {heatmapData.map((row, rowIdx) => (
              <div key={rowIdx} className="date-dock-row">
                {row.map((level, colIdx) => (
                  <div
                    key={colIdx}
                    className={`date-dock-cell ${
                      level ? `date-dock-${level}` : "date-dock-empty"
                    }`}
                    onMouseEnter={(e) => handleCellHover(rowIdx, colIdx, e)}
                    onMouseLeave={() => setHoveredCell(null)}
                  />
                ))}
              </div>
            ))}
          </div>

          <div className="date-dock-dates-row">
            {dates.map((date, idx) => (
              <div
                key={idx}
                className={`date-dock-date ${idx === 6 ? "highlight" : ""}`}
              >
                {date}
              </div>
            ))}
          </div>
        </div>
      </div>

      {hoveredCell && (
        <div
          className="date-dock-tooltip"
          style={{
            position: "fixed",
            left: `${tooltipPos.x}px`,
            top: `${tooltipPos.y}px`,
            transform: "translate(-50%, -100%)",
          }}
        >
          {hoveredCell.value}
        </div>
      )}
    </div>
  );
};

// Time-based Queue Prediction Component
const TimeQueuePrediction = ({
  docksList,
  onSelect,
  queuePredictionData = {},
}) => {
  const [selectedDay, setSelectedDay] = React.useState({});
  const [hoveredCell, setHoveredCell] = React.useState(null);
  const [tooltipPos, setTooltipPos] = React.useState({ x: 0, y: 0 });
  const dayScrollRef = React.useRef(null);

  const predications = queuePredictionData.predictions ?? [];
  // API data structure
  /*   const queuePredictionData = {
    dock_id: 22,
    predictions: [
      {
        date: "10 Jan",
        raw_date: "2026-01-10",
        hours: {
          "9am": 1,
          "10am": 1,
          "11am": 1,
          "12pm": 1,
          "1pm": 1,
          "2pm": 1,
          "3pm": 1,
          "4pm": 1,
          "5pm": 1,
          "6pm": 1,
          "7pm": 1,
          "8pm": 1,
        },
      },
      {
        date: "11 Jan",
        raw_date: "2026-01-11",
        hours: {
          "9am": 1,
          "10am": 1,
          "11am": 1,
          "12pm": 1,
          "1pm": 1,
          "2pm": 1,
          "3pm": 1,
          "4pm": 1,
          "5pm": 1,
          "6pm": 1,
          "7pm": 1,
          "8pm": 1,
        },
      },
      {
        date: "12 Jan",
        raw_date: "2026-01-12",
        hours: {
          "9am": 1,
          "10am": 1,
          "11am": 1,
          "12pm": 1,
          "1pm": 1,
          "2pm": 1,
          "3pm": 1,
          "4pm": 1,
          "5pm": 1,
          "6pm": 1,
          "7pm": 1,
          "8pm": 1,
        },
      },
      {
        date: "13 Jan",
        raw_date: "2026-01-13",
        hours: {
          "9am": 1,
          "10am": 1,
          "11am": 1,
          "12pm": 1,
          "1pm": 1,
          "2pm": 1,
          "3pm": 1,
          "4pm": 1,
          "5pm": 1,
          "6pm": 1,
          "7pm": 1,
          "8pm": 1,
        },
      },
      {
        date: "14 Jan",
        raw_date: "2026-01-14",
        hours: {
          "9am": 1,
          "10am": 1,
          "11am": 1,
          "12pm": 1,
          "1pm": 1,
          "2pm": 1,
          "3pm": 1,
          "4pm": 1,
          "5pm": 1,
          "6pm": 1,
          "7pm": 1,
          "8pm": 1,
        },
      },
      {
        date: "15 Jan",
        raw_date: "2026-01-15",
        hours: {
          "9am": 1,
          "10am": 1,
          "11am": 1,
          "12pm": 1,
          "1pm": 1,
          "2pm": 1,
          "3pm": 1,
          "4pm": 1,
          "5pm": 1,
          "6pm": 1,
          "7pm": 1,
          "8pm": 1,
        },
      },
      {
        date: "16 Jan",
        raw_date: "2026-01-16",
        hours: {
          "9am": 1,
          "10am": 1,
          "11am": 1,
          "12pm": 1,
          "1pm": 1,
          "2pm": 1,
          "3pm": 1,
          "4pm": 1,
          "5pm": 1,
          "6pm": 1,
          "7pm": 1,
          "8pm": 1,
        },
      },
    ],
    legend: {
      low: 1,
      moderate: 6,
      high: 10,
    },
  }; */

  // Extract dates from predictions
  const dates = predications.map((p) => p.date);

  // Extract time slots from first prediction's hours
  const timeSlots = Object.keys(predications[0]?.hours || {});

  // Function to determine level based on count and legend thresholds
  const getCountLevel = (count) => {
    if (count >= queuePredictionData.legend.high) return "high";
    if (count >= queuePredictionData.legend.moderate) return "moderate";
    return "normal";
  };

  // Transform predictions into heatmap format
  const heatmapData = predications.map((prediction) =>
    timeSlots.map((timeSlot) => getCountLevel(prediction.hours[timeSlot]))
  );

  // Calculate total vehicles for display
  const totalVehicles = predications.reduce(
    (sum, pred) => sum + Object.values(pred.hours).reduce((a, b) => a + b, 0),
    0
  );

  const getCellValue = (rowIdx, colIdx) => {
    const timeSlot = timeSlots[colIdx];
    return predications[rowIdx]?.hours[timeSlot] || 0;
  };

  const handleCellHover = (rowIdx, colIdx, event) => {
    const level = heatmapData[rowIdx][colIdx];
    if (level) {
      const rect = event.currentTarget.getBoundingClientRect();
      const count = getCellValue(rowIdx, colIdx);
      setHoveredCell({ value: count });
      setTooltipPos({
        x: rect.left + rect.width / 2,
        y: rect.top - 10,
      });
    }
  };

  const handleScrollDays = (direction) => {
    if (dayScrollRef.current) {
      const scrollAmount = 150; // Scroll by approximately 2 day buttons
      const currentScroll = dayScrollRef.current.scrollLeft;
      const targetScroll =
        direction === "left"
          ? currentScroll - scrollAmount
          : currentScroll + scrollAmount;

      dayScrollRef.current.scrollTo({
        left: targetScroll,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="fs-card time-queue-card">
      <div className="time-queue-top-section">
        <div className="time-queue-header">
          <div className="time-queue-title-section">
            <h3 className="time-queue-title">Time Based Queue Prediction</h3>
            <p className="time-queue-subtitle">Predicted data by past data</p>
          </div>
        </div>

        <div className="time-queue-day-selector-wrapper">
          <button
            className="day-nav-btn"
            onClick={() => handleScrollDays("left")}
          >
            <img src={prevIcon} alt="prev" />
          </button>

          <div className="time-queue-day-selector" ref={dayScrollRef}>
            {docksList.map((dock) => (
              <button
                key={dock.id}
                className={`day-btn ${
                  selectedDay.id === dock.id ? "active" : ""
                }`}
                onClick={() => {
                  setSelectedDay(dock);
                  onSelect(dock.id);
                }}
              >
                {dock.display_name}
              </button>
            ))}
          </div>

          <button
            className="day-nav-btn"
            onClick={() => handleScrollDays("right")}
          >
            <img src={nextIcon} alt="next" />
          </button>
        </div>
      </div>

      <div className="time-queue-stats-section mb-2">
        <div className="time-queue-stats">
          <div className="queue-count">{totalVehicles}</div>
          <div className="queue-text">
            vehicles will be recorded in{" "}
            <span className="highlight-day">
              {selectedDay.display_name || "Dock"}
            </span>
          </div>
        </div>

        <div className="time-queue-legend">
          <div className="legend-item">
            <span className="legend-dot legend-normal"></span>
            <span className="legend-text">Normal</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot legend-moderate"></span>
            <span className="legend-text">Moderate</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot legend-high"></span>
            <span className="legend-text">High</span>
          </div>
        </div>
      </div>

      <div className="time-queue-grid-container">
        <div className="time-queue-dates-column">
          {dates.map((date, idx) => (
            <div
              key={idx}
              className={`time-queue-date ${idx === 2 ? "highlight" : ""}`}
            >
              {date}
            </div>
          ))}
        </div>

        <div className="time-queue-content">
          <div className="time-queue-grid">
            {heatmapData.map((row, rowIdx) => (
              <div key={rowIdx} className="time-queue-row">
                {row.map((level, colIdx) => (
                  <div
                    key={colIdx}
                    className={`time-queue-cell time-queue-${level}`}
                    onMouseEnter={(e) => handleCellHover(rowIdx, colIdx, e)}
                    onMouseLeave={() => setHoveredCell(null)}
                  />
                ))}
              </div>
            ))}
          </div>

          <div className="time-queue-times-row">
            {timeSlots.map((time, idx) => (
              <div
                key={idx}
                className={`time-queue-time ${idx === 6 ? "highlight" : ""}`}
              >
                {time}
              </div>
            ))}
          </div>
        </div>
      </div>

      {hoveredCell && (
        <div
          className="time-queue-tooltip"
          style={{
            position: "fixed",
            left: `${tooltipPos.x}px`,
            top: `${tooltipPos.y}px`,
            transform: "translate(-50%, -100%)",
          }}
        >
          {hoveredCell.value}
        </div>
      )}
    </div>
  );
};

// Location Based Dock In & Dock Out Component
const LocationDockInOut = ({ locationPrediction = [] }) => {
  const [hoveredBar, setHoveredBar] = React.useState(null);
  const [tooltipPos, setTooltipPos] = React.useState({ x: 0, y: 0 });

  // API data structure
  /* const locationData = [
    {
      "location": "NCC-Chennai",
      "dock_in": 1327,
      "dock_out": 788
    },
    {
      "location": "LC1733376039369-Chennai",
      "dock_in": 91,
      "dock_out": 69
    },
    {
      "location": "Test/10/002-Chennai",
      "dock_in": 25,
      "dock_out": 12
    },
    {
      "location": "AK221-Chennai",
      "dock_in": 8,
      "dock_out": 0
    }
  ]; */

  // Transform API data to component format
  const locations = locationPrediction.map((loc) => ({
    name: loc.location,
    dockIn: loc.dock_in,
    dockOut: loc.dock_out,
  }));

  const maxValue = Math.max(
    ...locations.map((l) => Math.max(l.dockIn, l.dockOut))
  );

  const handleBarHover = (location, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setHoveredBar(location);
    setTooltipPos({
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    });
  };

  return (
    <div className="fs-card location-dock-card">
      <div className="location-dock-header">
        <h3 className="location-dock-title">
          Location Based Dock In & Dock Out
        </h3>
        <p className="location-dock-subtitle">Predicted data by past data</p>
      </div>

      <div className="location-dock-bars">
        {locations.map((location, idx) => (
          <div key={idx} className="location-bar-row">
            <div className="location-name">{location.name}</div>
            <div
              className="location-bars-container"
              onMouseEnter={(e) => handleBarHover(location, e)}
              onMouseLeave={() => setHoveredBar(null)}
            >
              <div
                className="location-bar dock-in-bar"
                style={{ width: `${(location.dockIn / maxValue) * 100}%` }}
              />
              <div
                className="location-bar dock-out-bar"
                style={{ width: `${(location.dockOut / maxValue) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {hoveredBar && (
        <div
          className="location-dock-tooltip"
          style={{
            position: "fixed",
            left: `${tooltipPos.x}px`,
            top: `${tooltipPos.y}px`,
            transform: "translate(-50%, -100%)",
          }}
        >
          <div className="tooltip-row">
            <span className="tooltip-dot dock-in-dot"></span>
            <span className="tooltip-label">Dock in</span>
            <span className="tooltip-separator">:</span>
            <span className="tooltip-value">{hoveredBar.dockIn}</span>
          </div>
          <div className="tooltip-row">
            <span className="tooltip-dot dock-out-dot"></span>
            <span className="tooltip-label">Dock out</span>
            <span className="tooltip-separator">:</span>
            <span className="tooltip-value">{hoveredBar.dockOut}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Location Based Free Docks Component
const LocationFreeDocks = ({
  locationList,
  locationFreePrediction = [],
  onLocationSelect,
}) => {
  const [selectedLocation, setSelectedLocation] = React.useState({});
  const locationScrollRef = React.useRef(null);

  // Extract dates from predictions
  const dates = locationFreePrediction.map((p) => p.date);

  // Extract time slots from first prediction's hours
  const timeSlots = Object.keys(locationFreePrediction[0]?.hours || {});

  // Transform predictions into grid format (array of arrays)
  const freeDocksData = locationFreePrediction.map((prediction) =>
    timeSlots.map((timeSlot) => prediction.hours[timeSlot])
  );

  const getCellColor = (value) => {
    if (value >= 20) return "high";
    if (value >= 10) return "moderate";
    if (value >= 5) return "normal";
    return "low";
  };

  const handleScrollLocations = (direction) => {
    if (locationScrollRef.current) {
      const scrollAmount = 200;
      const currentScroll = locationScrollRef.current.scrollLeft;
      const targetScroll =
        direction === "left"
          ? currentScroll - scrollAmount
          : currentScroll + scrollAmount;

      locationScrollRef.current.scrollTo({
        left: targetScroll,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="fs-card location-free-docks-card">
      <div className="location-free-header">
        <div className="time-queue-title-section">
          <h3 className="location-free-title">Location Based Free Docks</h3>
          <p className="location-free-subtitle">Predicted data by past data</p>
        </div>

        <div className="location-free-selector">
          <button
            className="location-nav-btn"
            onClick={() => handleScrollLocations("left")}
          >
            <img src={prevIcon} alt="prev" />
          </button>
          <div className="location-selector-scroll" ref={locationScrollRef}>
            {locationList.map((location) => (
              <button
                key={location.value}
                className={`location-btn ${
                  selectedLocation.value === location.value ? "active" : ""
                }`}
                onClick={() => {
                  setSelectedLocation(location);
                  onLocationSelect(location.value);
                }}
              >
                {location.label}
              </button>
            ))}
          </div>
          <button
            className="location-nav-btn"
            onClick={() => handleScrollLocations("right")}
          >
            <img src={nextIcon} alt="next" />
          </button>
        </div>
      </div>

      <div className="location-free-grid-container">
        <div className="location-free-dates-column">
          {dates.map((date, idx) => (
            <div
              key={idx}
              className={`location-free-date ${idx === 2 ? "highlight" : ""}`}
            >
              {date}
            </div>
          ))}
        </div>

        <div className="location-free-content">
          <div className="location-free-grid">
            {freeDocksData.map((row, rowIdx) => (
              <div
                key={rowIdx}
                className={`location-free-row ${
                  rowIdx % 2 === 0 ? "even" : "odd"
                }`}
              >
                {row.map((value, colIdx) => (
                  <div key={colIdx} className={`location-free-cell `}>
                    <span className="cell-value">{value}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="location-free-times-row">
            {timeSlots.map((time, idx) => (
              <div
                key={idx}
                className={`location-free-time ${idx === 6 ? "highlight" : ""}`}
              >
                {time}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Dashboard Component
export default function FuturisticDashboard() {
  const [availableDockPrediction, setAvailableDockPrediction] = useState([]);
  const [dockInPrediction, setDockInPrediction] = useState([]);
  const [docksList, setDocksList] = useState([]);
  const [queuePrediction, setQueuePrediction] = useState({});
  const [locationPrediction, setLocationPrediction] = useState([]);
  const [locationList, setLocationList] = useState([]);
  const [locationFreePrediction, setLocationFreePrediction] = useState([]);

  useEffect(() => {
    getAllDocks();
    getAvailableDockPrediction();
    getDockInPrediction();
    getLocationPrediction();
    getLocationList();
  }, []);

  const getAvailableDockPrediction = async () => {
    try {
      const response = await getAvailableDockPredictionApi("", "");

      if (response.data.success === true) {
        setAvailableDockPrediction(response.data.data ?? []);
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching category:", err);
    } finally {
    }
  };
  const getDockInPrediction = async () => {
    try {
      const response = await getDockInPredictionApi();

      if (response.data.success === true) {
        setDockInPrediction(response.data.data ?? []);
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching category:", err);
    } finally {
    }
  };
  const getAllDocks = async () => {
    try {
      const response = await getAllDocksApi();

      if (response.data.success === true) {
        setDocksList(response.data.docks ?? []);
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching category:", err);
    } finally {
    }
  };

  const getLocationPrediction = async (dockId) => {
    try {
      const response = await getLocationPredictionApi(dockId);

      if (response.data.success === true) {
        setLocationPrediction(response.data.data ?? []);
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching category:", err);
    } finally {
    }
  };

  const getQueuePrediction = async (dockId) => {
    try {
      const response = await getQueuePredictionApi(dockId);

      if (response.data.success === true) {
        setQueuePrediction(response.data.data ?? []);
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching category:", err);
    } finally {
    }
  };

  const getLocationList = async () => {
    try {
      const response = await getLocationListApi();

      if (response.data.success === true) {
        setLocationList(response.data.suggestionlist ?? []);
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching category:", err);
    } finally {
    }
  };
  const getLocationFreePrediction = async (locationId) => {
    try {
      const response = await getLocationFreePredictionApi(locationId);

      if (response.data.success === true) {
        setLocationFreePrediction(response.data.data ?? []);
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching location free prediction:", err);
    } finally {
    }
  };

  const graphData1 = [30, 50, 40, 60, 45];
  const graphData2 = [35, 45, 55, 50, 60];
  const graphData3 = [40, 55, 45, 65, 50];
  const graphData4 = [45, 60, 50, 70, 55];
  const graphData5 = [50, 65, 55, 75, 60];

  return (
    <div className="featurestic-dashboard">
      {/* Top Controls */}
      <div className="top-controls">
        <div className="toggle">
          <button className="btn">Default</button>
          <button className="btn primary">Futuristic</button>
        </div>
        <div className="date-actions">
          <button className="btn">📥 Download report</button>
          <button className="btn">🖨️ Print report</button>
          <button className="btn">📅 Show by date</button>
        </div>
      </div>

      {/* Available Chance Prediction */}
      <AvailableChancePrediction chancePrediction={availableDockPrediction} />

      {/* Dock Status Cards Row */}
      <div className="dock-status-row">
        <DockStatusCard
          title="Gate in"
          value="340"
          increase="45.6"
          peakDate="Feb 27, 2025"
          graphData={graphData1}
        />
        <DockStatusCard
          title="In Queue"
          value="340"
          increase="45.6"
          peakDate="Feb 27, 2025"
          graphData={graphData2}
        />
        <DockStatusCard
          title="Dock in"
          value="340"
          increase="45.6"
          peakDate="Feb 27, 2025"
          graphData={graphData3}
        />
        <DockStatusCard
          title="Dock out"
          value="340"
          increase="45.6"
          peakDate="Feb 27, 2025"
          graphData={graphData4}
        />
        <DockStatusCard
          title="Gate out"
          value="340"
          increase="45.6"
          peakDate="Feb 27, 2025"
          graphData={graphData5}
        />
      </div>

      {/* Prediction Grids Row */}
      <div className="prediction-grids-row">
        <DateBasedDockPrediction predictionData={dockInPrediction} />
        <TimeQueuePrediction
          docksList={docksList}
          onSelect={(dockId) => getQueuePrediction(dockId)}
          queuePredictionData={queuePrediction}
        />
      </div>

      {/* Location Based Predictions Row */}
      <div className="location-predictions-row">
        <LocationDockInOut locationPrediction={locationPrediction} />
        <LocationFreeDocks
          locationList={locationList}
          locationFreePrediction={locationFreePrediction}
          onLocationSelect={(locationId) =>
            getLocationFreePrediction(locationId)
          }
        />
      </div>
    </div>
  );
}
